import { controlPoints, isBusinessOnly, nextFixes, planActions, planMissions } from '@cyberscore/contracts';
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useSession, can } from '../../lib/session';
import { useAssessments, type AssessmentSummary } from '../assessments/api';
import { useScore } from '../portfolio/api';
import { requirements } from './progress';

/** A business on its own (decision 0034), or someone an insurer added to answer for their business. */
export function useIsBusiness() {
  const { tenant } = useSession();
  if (tenant?.kind === 'business') return true;
  // Anyone who may do no more than a business's own people sees the business app.
  return !!tenant && tenant.kind !== 'platform' && tenant.permissions.length > 0 && isBusinessOnly(tenant.permissions);
} // prettier-ignore

/** The business is its own tenant and this person runs it: they start checks and finish them themselves. */
export function useOwnsBusiness() {
  const { tenant } = useSession();
  return tenant?.kind === 'business' && can(tenant, 'assessments.start_own');
}

interface BusinessValue {
  /** Newest first. */
  assessments: AssessmentSummary[];
  /** The assessment the business pages show: the one picked, else the newest still open, else the newest. */
  current: AssessmentSummary | null;
  choose: (id: string) => void;
  loading: boolean;
  error: Error | null;
}

const Ctx = createContext<BusinessValue | null>(null);
const KEY = 'cyberscore.business.assessment';

const read = (tenant: string) => {
  try {
    return localStorage.getItem(`${KEY}.${tenant}`);
  } catch {
    return null;
  }
};

/** Which of the business's assessments the pages show, remembered per tenant on this device. */
export function BusinessProvider({ children }: { children: ReactNode }) {
  const { tenant } = useSession();
  const list = useAssessments();
  const [picked, setPicked] = useState<string | null>(() => (tenant ? read(tenant.id) : null));
  const choose = useCallback(
    (id: string) => {
      setPicked(id);
      try {
        if (tenant) localStorage.setItem(`${KEY}.${tenant.id}`, id);
      } catch {
        /* storage unavailable: the choice lasts this visit */
      }
    },
    [tenant],
  );
  const value = useMemo<BusinessValue>(() => {
    const assessments = [...(list.data ?? [])].sort((a, b) => b.updated_at.localeCompare(a.updated_at));
    return {
      assessments,
      current:
        assessments.find((a) => a.id === picked) ??
        // Changes the insurer asked for come first: the business has something to do there.
        assessments.find((a) => a.status === 'in_remediation') ??
        assessments.find((a) => a.status === 'in_progress') ??
        assessments[0] ??
        null,
      choose,
      loading: list.isPending,
      error: list.error,
    };
  }, [list.data, list.isPending, list.error, picked, choose]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useBusiness() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useBusiness outside BusinessProvider');
  return v;
}

/** The current assessment's score with what the pages derive from it: missions, the plan, requirements. */
export function useBusinessScore() {
  const { current } = useBusiness();
  const view = useScore(current?.id ?? '');
  const derived = useMemo(() => {
    if (!view.data) return null;
    const { score: r, explanation: trace } = view.data;
    const missions = planMissions(trace);
    return {
      missions,
      plan: planActions(trace),
      next: nextFixes(trace),
      points: controlPoints(trace),
      requirements: requirements(r, trace),
    };
  }, [view.data]);
  return { current, view, derived };
}
